import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SaveIcon from "@mui/icons-material/Save";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import api from "../../services/api/axios";
import { adminDropdownSx, adminDropdownMenuProps } from "./adminDropdownStyles";
import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";
import DeleteConfirmDialog from "../../shared/components/DeleteConfirmDialog";

const emptyForm = {
  title: "",
  article_url: "",
  content_type: "article",
  description: "",
  image_url: "",
  source_name: "",
  published_date: "",
  display_order: 0,
  is_active: true,
};

function formatDate(val) {
  if (!val) return "-";
  const d = new Date(`${val}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString();
}

function validateUrl(value) {
  if (!value) return true;
  return /^https?:\/\/\S+$/i.test(value);
}

export default function AdminLandingPageArticles() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    msg: "",
    severity: "success",
  });

  const fetchArticles = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/api/admin/landing-page/articles");
      setArticles(res.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load articles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError("");
  };

  const openEdit = (article) => {
    setEditingId(article.id);
    setForm({
      title: article.title || "",
      article_url: article.article_url || "",
      content_type: article.content_type || "article",
      description: article.description || "",
      image_url: article.image_url || "",
      source_name: article.source_name || "",
      published_date: article.published_date || "",
      display_order: article.display_order ?? 0,
      is_active: Boolean(article.is_active),
    });
    setFormError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const updateField = (field) => (e) => {
    const value =
      field === "is_active"
        ? e.target.checked
        : field === "display_order"
          ? Number(e.target.value)
          : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleSave = async () => {
    if (saving) return;

    const title = form.title.trim();
    const articleUrl = form.article_url.trim();
    const imageUrl = form.image_url.trim();

    const contentType = form.content_type === "white_paper" ? "white_paper" : "article";
    const itemLabel = contentType === "white_paper" ? "White paper" : "Article";

    if (!title || !articleUrl) {
      setFormError(`${itemLabel} title and URL are required.`);
      return;
    }
    if (!validateUrl(articleUrl)) {
      setFormError(`${itemLabel} URL must start with http:// or https://.`);
      return;
    }
    if (imageUrl && !validateUrl(imageUrl)) {
      setFormError("Image URL must start with http:// or https://.");
      return;
    }

    setSaving(true);
    setFormError("");
    try {
      const payload = {
        title,
        article_url: articleUrl,
        content_type: contentType,
        description: form.description.trim() || undefined,
        image_url: imageUrl || undefined,
        source_name: form.source_name.trim() || undefined,
        published_date: form.published_date || undefined,
        display_order: Number.isFinite(form.display_order) ? form.display_order : 0,
        is_active: form.is_active,
      };

      if (editingId) {
        await api.put(`/api/admin/landing-page/articles/${editingId}`, payload);
        setSnackbar({ open: true, msg: `${itemLabel} updated.`, severity: "success" });
      } else {
        await api.post("/api/admin/landing-page/articles", payload);
        setSnackbar({ open: true, msg: `${itemLabel} created.`, severity: "success" });
      }
      resetForm();
      await fetchArticles();
    } catch (e) {
      const message = e?.response?.data?.detail || "Failed to save article";
      setFormError(message);
      setSnackbar({ open: true, msg: message, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (deleting || !deleteTarget) return;

    setDeleting(true);
    try {
      await api.delete(`/api/admin/landing-page/articles/${deleteTarget.id}`);
      setSnackbar({ open: true, msg: "Article deleted.", severity: "success" });
      setDeleteTarget(null);
      await fetchArticles();
      if (editingId === deleteTarget.id) resetForm();
    } catch (e) {
      setSnackbar({
        open: true,
        msg: e?.response?.data?.detail || "Failed to delete article",
        severity: "error",
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ color: BRAND_BLUE, mb: 1 }}>
        Landing Page Articles
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Paper sx={{ p: 2.5, border: `2px solid ${BRAND_BLUE}`, borderRadius: 4 }}>
        <Stack spacing={2}>
          <Typography variant="subtitle1" sx={{ color: BRAND_BLUE, fontWeight: 800 }}>
            {editingId ? "Edit Content" : "Add Content"}
          </Typography>

          {formError ? <Alert severity="error">{formError}</Alert> : null}

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            }}
          >
            <TextField
              select
              sx={adminDropdownSx}
              SelectProps={{ MenuProps: adminDropdownMenuProps }}
              label="Content type"
              fullWidth
              value={form.content_type}
              onChange={updateField("content_type")}
            >
              <MenuItem value="article">Article</MenuItem>
              <MenuItem value="white_paper">White Paper</MenuItem>
            </TextField>
            <TextField label="Title" required fullWidth value={form.title} onChange={updateField("title")} />
            <TextField
              label={form.content_type === "white_paper" ? "Download URL" : "Article URL"}
              required
              fullWidth
              placeholder={form.content_type === "white_paper" ? "https://example.com/white-paper.pdf" : "https://example.com/article"}
              value={form.article_url}
              onChange={updateField("article_url")}
              sx={{ gridColumn: { xs: "auto", md: "1 / -1" } }}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={3}
              value={form.description}
              onChange={updateField("description")}
              sx={{ gridColumn: { xs: "auto", md: "1 / -1" } }}
            />
            <TextField label="Image URL" fullWidth value={form.image_url} onChange={updateField("image_url")} />
            <TextField label="Source name" fullWidth value={form.source_name} onChange={updateField("source_name")} />
            <TextField
              label="Published date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.published_date}
              onChange={updateField("published_date")}
            />
            <TextField
              label="Display order"
              type="number"
              fullWidth
              value={form.display_order}
              onChange={updateField("display_order")}
            />
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
            <FormControlLabel
              control={<Checkbox icon={<RadioButtonUncheckedIcon />} checkedIcon={<CheckCircleIcon />} indeterminateIcon={<RemoveCircleIcon />} checked={form.is_active} onChange={updateField("is_active")} color="success" />}
              label="Active"
            />
            <Box sx={{ flex: 1 }} />
            {editingId ? (
              <Button onClick={resetForm} disabled={saving}>
                Cancel Edit
              </Button>
            ) : null}
            <Button
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving || !form.title.trim() || !form.article_url.trim()}
              sx={{ bgcolor: BRAND_GREEN, color: "white", "&:hover": { bgcolor: "#546e1b" } }}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ mt: 2, border: `2px solid ${BRAND_BLUE}`, borderRadius: 4, overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 980 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: BRAND_BLUE }}>Title</TableCell>
              <TableCell sx={{ color: BRAND_BLUE }}>Type</TableCell>
              <TableCell sx={{ color: BRAND_BLUE }}>Source</TableCell>
              <TableCell sx={{ color: BRAND_BLUE }}>Published date</TableCell>
              <TableCell sx={{ color: BRAND_BLUE }}>Active status</TableCell>
              <TableCell sx={{ color: BRAND_BLUE }}>Display order</TableCell>
              <TableCell sx={{ color: BRAND_BLUE }}>Link</TableCell>
              <TableCell align="right" sx={{ color: BRAND_BLUE }}>Edit</TableCell>
              <TableCell align="right" sx={{ color: BRAND_BLUE }}>Delete</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {articles.map((article) => (
              <TableRow key={article.id}>
                <TableCell sx={{ color: BRAND_BLUE }}>{article.title}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={article.content_type === "white_paper" ? "White Paper" : "Article"}
                    sx={{
                      bgcolor: article.content_type === "white_paper" ? BRAND_GREEN : BRAND_BLUE,
                      color: "white",
                      fontWeight: 700,
                    }}
                  />
                </TableCell>
                <TableCell sx={{ color: BRAND_BLUE }}>{article.source_name || "-"}</TableCell>
                <TableCell sx={{ color: BRAND_BLUE }}>{formatDate(article.published_date)}</TableCell>
                <TableCell>
                  {article.is_active ? (
                    <Chip size="small" label="Active" color="success" />
                  ) : (
                    <Chip size="small" label="Inactive" variant="outlined" />
                  )}
                </TableCell>
                <TableCell sx={{ color: BRAND_BLUE }}>{article.display_order}</TableCell>
                <TableCell>
                  <Button
                    size="small"
                    component="a"
                    href={article.article_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    endIcon={<OpenInNewIcon fontSize="inherit" />}
                  >
                    {article.content_type === "white_paper" ? "Download" : "Open"}
                  </Button>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(article)} aria-label="Edit article">
                    <EditIcon fontSize="small" sx={{ color: BRAND_BLUE }} />
                  </IconButton>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setDeleteTarget(article)} aria-label="Delete article">
                    <DeleteIcon fontSize="small" color="error" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            ) : null}

            {!loading && articles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No landing page content yet
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Paper>

      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        loading={deleting}
        title="Delete Article"
        description={`This will permanently delete "${deleteTarget?.title}" and remove it from the public landing page articles and white papers section.`}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
